import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Descriptions, Form, Input, Modal, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { FileDoneOutlined, ReloadOutlined, RollbackOutlined, SaveOutlined } from '@ant-design/icons';
import { SalesDocumentFileRequest } from '../store/user/types';
import { formatUtcDate } from '../services/dateTime';

const { Text } = Typography;

interface PaymentRequest {
    id: string;
    organizationName: string;
    organizationEmail: string;
    organizationPhone: string;
    clientRuc: string;
    clientName: string;
    clientEmail: string;
    planName: string;
    generatedContractNumber?: number;
    providerTransactionId?: string;
    authorizationCode?: string;
    status: string;
    invoiceStatus: string;
    billingPeriods: number;
    currency: string;
    subtotal: number;
    iva: number;
    total: number;
    paidAt?: string;
}

interface Props {
    getPendingPaymentRequestInvoices: (organizationId?: string) => Promise<PaymentRequest[]>;
    postPaymentRequestSalesDocument: (paymentRequestId: string, value: any) => Promise<any>;
    reversePaymentRequestPayPhone: (paymentRequestId: string) => Promise<any>;
}

const money = (value: number, currency = 'USD') =>
    new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    }).format(value || 0);

const fileToBase64 = (file: File): Promise<SalesDocumentFileRequest> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = String(reader.result || '');
        const base64File = result.includes(',') ? result.split(',')[1] : result;
        resolve({
            base64File,
            fileName: file.name
        });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const PlanBillingPendingInvoices: React.FC<Props> = (props) => {
    const [form] = Form.useForm();
    const [rows, setRows] = useState<PaymentRequest[]>([]);
    const [selected, setSelected] = useState<PaymentRequest | null>(null);
    const [documentFile, setDocumentFile] = useState<SalesDocumentFileRequest | null>(null);
    const [rideFile, setRideFile] = useState<SalesDocumentFileRequest | null>(null);

    const loadRows = useCallback(async () => {
        const result = await props.getPendingPaymentRequestInvoices();
        setRows(result || []);
    }, [props]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const handleOpen = (row: PaymentRequest) => {
        setSelected(row);
        setDocumentFile(null);
        setRideFile(null);
        form.resetFields();
        form.setFieldsValue({
            documentType: 'FACTURA',
            statusSri: 'MANUAL',
            integrationSri: true
        });
    };

    const handleSubmit = async () => {
        if (!selected) return;

        const values = await form.validateFields();
        const response = await props.postPaymentRequestSalesDocument(selected.id, {
            number: values.number,
            documentType: values.documentType,
            statusSri: values.statusSri,
            integrationSri: values.integrationSri,
            authorization: values.authorization,
            documentFile,
            rideFile
        });

        if (response) {
            setSelected(null);
            await loadRows();
        }
    };

    const handleReverse = (row: PaymentRequest) => {
        Modal.confirm({
            title: 'Reversar pago PayPhone',
            content: `Se reversara el pago de ${money(row.total, row.currency)} y se anulara el contrato #${row.generatedContractNumber || 'generado'}.`,
            okText: 'Reversar pago',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: async () => {
                const response = await props.reversePaymentRequestPayPhone(row.id);
                if (response) {
                    await loadRows();
                }
            }
        });
    };

    return (
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="Pagos aprobados pendientes de factura"
                description="Aqui se cargan manualmente el numero, la autorizacion, el documento y el RIDE. Al guardar, el pago desaparece de esta bandeja."
            />

            <Button icon={<ReloadOutlined />} onClick={loadRows}>
                Actualizar
            </Button>

            <Table
                rowKey="id"
                dataSource={rows}
                pagination={{ pageSize: 8 }}
                scroll={{ x: 900 }}
                columns={[
                    {
                        title: 'Organizacion',
                        key: 'organization',
                        render: (_: any, row: PaymentRequest) => (
                            <Space direction="vertical" size={0}>
                                <Text strong>{row.organizationName}</Text>
                                <Text type="secondary">{row.organizationEmail}</Text>
                                <Text type="secondary">Tel: {row.organizationPhone || 'No registrado'}</Text>
                            </Space>
                        )
                    },
                    {
                        title: 'Plan',
                        key: 'plan',
                        render: (_: any, row: PaymentRequest) => (
                            <Space direction="vertical" size={0}>
                                <Text>{row.planName}</Text>
                                <Text type="secondary">Contrato #{row.generatedContractNumber || 'Generado'}</Text>
                            </Space>
                        )
                    },
                    {
                        title: 'Pago',
                        key: 'payment',
                        render: (_: any, row: PaymentRequest) => (
                            <Space direction="vertical" size={0}>
                                <Tag color="green">{row.status}</Tag>
                                <Text type="secondary">{formatUtcDate(row.paidAt)}</Text>
                            </Space>
                        )
                    },
                    {
                        title: 'Total',
                        dataIndex: 'total',
                        key: 'total',
                        align: 'right' as const,
                        render: (value: number, row: PaymentRequest) => money(value, row.currency)
                    },
                    {
                        title: 'Factura',
                        key: 'invoice',
                        render: (_: any, row: PaymentRequest) => <Tag color="gold">{row.invoiceStatus}</Tag>
                    },
                    {
                        title: '',
                        key: 'actions',
                        align: 'right' as const,
                        render: (_: any, row: PaymentRequest) => (
                            <Space>
                                <Button danger icon={<RollbackOutlined />} onClick={() => handleReverse(row)}>
                                    Reversar pago
                                </Button>
                                <Button icon={<FileDoneOutlined />} onClick={() => handleOpen(row)}>
                                    Emitir factura
                                </Button>
                            </Space>
                        )
                    }
                ]}
            />

            <Modal
                open={!!selected}
                title="Emitir factura manual"
                width={980}
                onCancel={() => setSelected(null)}
                onOk={handleSubmit}
                okText="Guardar"
                cancelText="Cancelar"
                destroyOnHidden
                okButtonProps={{ icon: <SaveOutlined /> }}
            >
                {selected && (
                    <Space direction="vertical" size={18} style={{ width: '100%' }}>
                        <Descriptions bordered size="small" column={{ xs: 1, md: 2 }}>
                            <Descriptions.Item label="Organizacion">{selected.organizationName}</Descriptions.Item>
                            <Descriptions.Item label="Cliente">{selected.clientName}</Descriptions.Item>
                            <Descriptions.Item label="RUC">{selected.clientRuc}</Descriptions.Item>
                            <Descriptions.Item label="Plan">{selected.planName}</Descriptions.Item>
                            <Descriptions.Item label="Autorizacion PayPhone">{selected.authorizationCode || 'No registrado'}</Descriptions.Item>
                            <Descriptions.Item label="Transaccion">{selected.providerTransactionId || 'No registrado'}</Descriptions.Item>
                            <Descriptions.Item label="Total">
                                <Text strong>{money(selected.total, selected.currency)}</Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <Form form={form} layout="vertical">
                            <Row gutter={[16, 12]}>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Numero" name="number" rules={[{ required: true, message: 'Ingrese el numero' }]}>
                                        <Input placeholder="001-001-000000123" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Tipo" name="documentType" rules={[{ required: true, message: 'Seleccione el tipo' }]}>
                                        <Select
                                            options={[
                                                { value: 'FACTURA', label: 'FACTURA' },
                                                { value: 'NVTA', label: 'NVTA' }
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Estado SRI" name="statusSri" rules={[{ required: true, message: 'Seleccione el estado SRI' }]}>
                                        <Select
                                            options={[
                                                { value: 'MANUAL', label: 'MANUAL' },
                                                { value: 'PENDIENTE', label: 'PENDIENTE' },
                                                { value: 'SERVICIO', label: 'SERVICIO' }
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item label="Integracion SRI" name="integrationSri" rules={[{ required: true, message: 'Seleccione integracion' }]}>
                                        <Select
                                            options={[
                                                { value: true, label: 'SI' },
                                                { value: false, label: 'NO' }
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={16}>
                                    <Form.Item label="Autorizacion" name="authorization" rules={[{ required: true, message: 'Ingrese la autorizacion' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Documento">
                                        <input
                                            type="file"
                                            onChange={async (event) => {
                                                const file = event.target.files?.[0];
                                                setDocumentFile(file ? await fileToBase64(file) : null);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="RIDE"
                                        required
                                        validateStatus={!rideFile ? 'error' : undefined}
                                        help={!rideFile ? 'Seleccione el archivo RIDE' : undefined}
                                    >
                                        <input
                                            type="file"
                                            onChange={async (event) => {
                                                const file = event.target.files?.[0];
                                                setRideFile(file ? await fileToBase64(file) : null);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </Space>
                )}
            </Modal>
        </Space>
    );
};

export default PlanBillingPendingInvoices;
